/**
 * What the restaurant actually published about a dish.
 *
 * The API keeps numeric placeholders (prep time 0, spice 0, serves 1) for fields a
 * restaurant never states; `details.unrecorded_fields` marks them. Guest surfaces
 * must treat those as unknown, never as "instant", "mild" or "single serving".
 */
import { toPersianDigits } from '@/lib/formatting';
import type { MenuItemOut } from '@/types/api';

type Unrecorded = NonNullable<NonNullable<MenuItemOut['details']>['unrecorded_fields']>[number];

export function isRecorded(item: MenuItemOut, field: Unrecorded): boolean {
  return !item.details?.unrecorded_fields?.includes(field);
}

export function prepMinutes(item: MenuItemOut): number | null {
  return isRecorded(item, 'preparation_minutes') ? item.preparation_minutes : null;
}

export function spiceLevel(item: MenuItemOut): number | null {
  return isRecorded(item, 'spice_level') ? item.spice_level : null;
}

export function servesCount(item: MenuItemOut): number | null {
  return isRecorded(item, 'serves_count') ? item.serves_count : null;
}

const KCAL = /([۰-۹0-9]+(?:[.٫][۰-۹0-9]+)?)\s*کیلوکالری/;

/** Energy in kcal as printed on the official menu, or null when it prints none. */
export function kcalOf(item: MenuItemOut): number | null {
  const match = KCAL.exec(item.details?.nutrition_notes ?? '');
  if (!match?.[1]) return null;
  const value = Number(match[1].replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit))).replace('٫', '.'));
  return Number.isFinite(value) ? value : null;
}

export function kcalLabel(item: MenuItemOut): string | null {
  const kcal = kcalOf(item);
  return kcal === null ? null : `${toPersianDigits(kcal)} کیلوکالری`;
}
